import { useEffect, useRef, useState } from "react";
import { ArrowUp } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Floating "back to top" button. Becomes visible after the user has scrolled
 * past the first viewport and smoothly scrolls back to the top of the page.
 *
 * Uses an IntersectionObserver on a top sentinel so it works regardless of
 * which element is the scroll container (window, html, or an ancestor with
 * `overflow-x: hidden` that becomes a scroller in some browsers).
 */
export function BackToTop() {
  const [visible, setVisible] = useState(false);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      ([entry]) => setVisible(!entry.isIntersecting),
      { threshold: 0 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const handleClick = () => {
    const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    window.scrollTo({ top: 0, behavior: prefersReduced ? "auto" : "smooth" });
    // Also reset the documentElement / body scroll in case window scroll is a no-op.
    document.documentElement.scrollTo?.({ top: 0, behavior: prefersReduced ? "auto" : "smooth" });
  };

  return (
    <>
      {/* Sentinel placed near the top of the page. While visible we are at
          the top, so the button is hidden. Once scrolled past, it appears. */}
      <div
        ref={sentinelRef}
        aria-hidden="true"
        className="pointer-events-none absolute top-[600px] left-0 h-px w-px"
      />
      <button
      type="button"
      aria-label="Voltar ao topo"
      onClick={handleClick}
      className={cn(
        "fixed bottom-6 right-6 z-50 inline-flex size-12 items-center justify-center",
        "rounded-full bg-gradient-primary text-primary-foreground shadow-glow",
        "border border-primary/30 backdrop-blur-xl",
        "transition-all duration-300 hover:opacity-90 hover:scale-105",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        visible
          ? "opacity-100 translate-y-0 pointer-events-auto"
          : "opacity-0 translate-y-4 pointer-events-none",
      )}
    >
      <ArrowUp className="size-5" />
      </button>
    </>
  );
}